import React, { useState, useEffect } from 'react';
import api from '../../../service/apiService';
import './AccountModal.css';

const AccountModal = ({ isOpen, onClose, onSuccess }) => {
  const [accountData, setAccountData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    address: '',
    phone_number: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchAccountData();
    }
  }, [isOpen]);

  const fetchAccountData = async () => {
    setIsLoading(true);
    try {
      const data = await api.getAccount();

      setAccountData({
        email: data.account?.email || '',
        first_name: data.account?.first_name || '',
        last_name: data.account?.last_name || '',
        address: data.account?.address || '',
        phone_number: data.account?.phone_number || '',
      });

      setErrors({});
    } catch (error) {
      console.error('Failed to fetch account data:', error);
      setErrors({ general: 'Failed to load account information' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAccountData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Phone number validation (optional field)
    if (accountData.phone_number && !/^[+]?[1-9][\d]{0,15}$/.test(accountData.phone_number)) {
      newErrors.phone_number = 'Please enter a valid phone number';
    }

    // Address length validation (optional field)
    if (accountData.address && accountData.address.length > 255) {
      newErrors.address = 'Address must be less than 255 characters';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSaving(true);
    setErrors({});

    try {
      // Only send the fields that can be updated
      const updateData = {
        address: accountData.address,
        phone_number: accountData.phone_number,
      };

      await api.updateAccount(updateData);

      // Close modal and show success message
      onClose();
      if (onSuccess) {
        onSuccess('Account information updated successfully!');
      }
    } catch (error) {
      setErrors({ general: error.message || 'Failed to update account information' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Account Information</h2>
          <button className="modal-close" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading account information...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="account-form">
              {errors.general && <div className="error-banner">{errors.general}</div>}

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="first_name">First Name</label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={accountData.first_name}
                    disabled
                    className="disabled-input"
                  />
                  <small className="field-note">Contact support to change your name</small>
                </div>

                <div className="form-group">
                  <label htmlFor="last_name">Last Name</label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={accountData.last_name}
                    disabled
                    className="disabled-input"
                  />
                  <small className="field-note">Contact support to change your name</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={accountData.email}
                  disabled
                  className="disabled-input"
                />
                <small className="field-note">Contact support to change your email</small>
              </div>

              <div className="form-group">
                <label htmlFor="phone_number">Phone Number</label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={accountData.phone_number}
                  onChange={handleChange}
                  className={errors.phone_number ? 'error' : ''}
                  placeholder="Enter your phone number"
                  disabled={isSaving}
                />
                {errors.phone_number && (
                  <span className="error-message">{errors.phone_number}</span>
                )}
                <small className="field-note">Format: +1234567890 or 1234567890</small>
              </div>

              <div className="form-group">
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={accountData.address}
                  onChange={handleChange}
                  className={errors.address ? 'error' : ''}
                  placeholder="Enter your address"
                  rows="3"
                  maxLength="255"
                  disabled={isSaving}
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
                <small className="field-note">{accountData.address.length}/255 characters</small>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-button"
                  onClick={handleClose}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="save-button" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountModal;
